<template>
  <div :class="[{ wheel: true }, theme]">
    <div class="border">
      <div class="content">
        <svg
          :id="id"
          :style="{
            transform: `rotate(${currentRotation}deg)`,
            transformOrigin: 'center center',
            transition: 'transform 3s cubic-bezier(0.18, 0, 0.4, 1)',
            fontSize: `${fontSize || '16px'}`
          }"
          preserveAspectRatio="xMinYMin meet"
          viewBox="0 0 500 500"
        >
          <defs></defs>
          <g transform="translate(250, 250)"></g>
        </svg>
      </div>
      <button
        class="spin-button"
        @click="
          resultVisible = false;
          spin();
        "
      ></button>
    </div>
    <div :id="arrowId" class="arrow"></div>
    <div
      v-show="resultVisible"
      class="result"
      @click="
        resultVisible = false;
        spin();
      "
    >
      {{ selected }}
    </div>
    <div class="decorations">
      <span class="decoration"></span>
      <span class="decoration"></span>
      <span class="decoration"></span>
      <span class="decoration"></span>
      <span class="decoration"></span>
      <span class="decoration"></span>
      <span class="decoration"></span>
      <span class="decoration"></span>
      <span class="decoration"></span>
      <span class="decoration"></span>
      <span class="decoration"></span>
    </div>
  </div>
</template>

<script>
import * as d3 from "d3";
import defaultColors from "./data/defaultColors";

export default {
  name: "Wheel",
  props: {
    id: String,
    theme: String,
    sections: Object,
    showImage: Boolean,
    showType: Boolean,
    showText: Boolean,
    noBorder: Boolean,
    showResult: Boolean,
    fontSize: Number
  },
  data() {
    return {
      currentRotation: 0,
      selected: "",
      resultVisible: false
    };
  },
  computed: {
    filteredData() {
      return this.sections.filter(c => c.active);
    },
    arrowId() {
      return `${this.id}-arrow`;
    }
  },
  mounted() {
    this.generate();
    const el = document.querySelector(`#${this.id}`);
    const arrow = document.querySelector(`#${this.arrowId}`);
    el.addEventListener("transitionstart", () => {
      arrow.classList.add("spinning");
    });
    el.addEventListener("transitionend", () => {
      arrow.classList.remove("spinning");
    });
  },
  watch: {
    filteredData: function() {
      this.update();
    }
  },
  methods: {
    formatName(name) {
      return name
        .split(" ")
        .join("-")
        .replace("(", "")
        .replace(")", "");
    },
    generate() {
      this.addGradients();
      this.render();
    },
    addGradients() {
      const defs = d3.select(`#${this.id}`).select("defs");

      this.filteredData.forEach((section, i) => {
        if (section.color) {
          const lg = defs
            .append("radialGradient")
            .attr("id", `gradient-${this.formatName(section.name)}-${i}`)
            .attr("cx", "0%")
            .attr("cy", "0%")
            .attr("r", "50%")
            .attr("gradientUnits", "userSpaceOnUse");

          lg.append("stop")
            .attr("offset", "0%")
            .attr("stop-color", section.color[0]);
          lg.append("stop")
            .attr("offset", "75%")
            .attr("stop-color", section.color[0]);
          lg.append("stop")
            .attr("offset", "100%")
            .attr("stop-color", section.color[1]);
        }
      });
    },
    render() {
      const sectionAngle = 360 / this.filteredData.length;
      const pie = d3.pie();
      const arc = d3
        .arc()
        .innerRadius(0)
        .outerRadius(248);

      const arcs = d3
        .select(`#${this.id}`)
        .select("g")
        .selectAll("section")
        .data(pie(this.filteredData.map(d => d.value)))
        .enter()
        .append("g")
        .attr(
          "class",
          (d, i) => `section ${this.formatName(this.filteredData[i].name)}`
        );

      if (this.noBorder) {
        arcs
          .append("path")
          .attr("fill", (d, i) => {
            if (this.filteredData[i].color) {
              return `url(#gradient-${this.formatName(
                this.filteredData[i].name
              )}-${i})`;
            } else {
              return defaultColors[i % defaultColors.length];
            }
          })
          .attr("d", arc);
      } else {
        arcs
          .append("path")
          .attr("fill", (d, i) => {
            if (this.filteredData[i].color) {
              return `url(#gradient-${this.formatName(
                this.filteredData[i].name
              )}-${i})`;
            } else {
              return defaultColors[i % defaultColors.length];
            }
          })
          .attr("stroke", "white")
          .attr("stroke-width", "2px")
          .attr("d", arc);
      }

      if (this.showImage) {
        arcs
          .append("image")
          .attr("href", (d, i) => {
            return require(`./assets/${this.filteredData[i].image}`);
          })
          .attr("height", "70")
          .attr("width", "70")
          .attr("transform", (d, i) => {
            return `rotate(${sectionAngle * i +
              sectionAngle / 2}) translate(-35 -200)`;
          });
      }

      if (this.showType) {
        d3.select(`#${this.id}`)
          .select("g")
          .append("image")
          .attr("href", require("./assets/types.png"))
          .attr("height", "418")
          .attr("width", "418")
          .attr("transform", "rotate(7.5) translate(-209, -209)");
      }

      if (this.showText) {
        arcs
          .append("text")
          .attr("transform", d => {
            d.innerRadius = 0;
            d.outerRadius = 248;
            d.angle = (d.startAngle + d.endAngle) / 2;
            return `rotate(${(d.angle * 180) / Math.PI -
              90}) translate(${d.outerRadius - 10} 5)`;
          })
          .attr("text-anchor", "end")
          .attr("fill", "white")
          .text((d, i) => {
            return this.filteredData[i].name;
          });
      }
    },
    update() {
      d3.select(`#${this.id}`)
        .select("g")
        .selectAll("*")
        .remove();
      this.render();
    },
    spin() {
      const sectionAngle = 360 / this.filteredData.length;
      const min = 360 * 5;
      const extra = 5;

      this.currentRotation +=
        min +
        Math.round(Math.random() * extra) * 360 +
        Math.round(Math.random() * 360);

      const index =
        this.filteredData.length -
        Math.ceil((this.currentRotation % 360) / sectionAngle);

      this.selected = this.filteredData[index].name;

      if (this.showResult) {
        setTimeout(() => (this.resultVisible = true), 3000);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@keyframes pulse {
  0% {
    opacity: 0.8;
  }
  100% {
    opacity: 1;
  }
}

.result {
  position: absolute;
  font-family: "Matrix";
  padding: 20px 40px;
  width: calc(100% - 10px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 50px;
  top: 50%;
  left: calc(50% + 5px);
  z-index: 3;
  transform: translate3d(-50%, -50%, 0);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-weight: bold;
  border-radius: 10px 10px 10px 10px / 40px 40px 40px 40px;
  cursor: pointer;
}

.wheel {
  position: relative;
  width: 100%;
  padding-bottom: 100%;

  &.silver {
    .spin-button {
      background-image: url("./assets/center-silver.png");
    }

    .border::before {
      width: 16%;
      height: 14.8%;
      top: -2.5%;
      background-image: url("./assets/arrow-silver.png");
    }

    .arrow {
      display: none;
    }

    .decoration {
      background-image: radial-gradient(
          circle at 0px 0px,
          #fff,
          #fff 30%,
          transparent 60%
        ),
        radial-gradient(circle at 0px 0px, #63dfe0, #63dfe0 50%, #000000 100%);
    }
  }

  @media screen and (min-width: 680px) {
    width: 600px;
    height: 600px;
    padding: 0;
  }
}

.decoration {
  position: absolute;
  width: 2.4%;
  height: 2.4%;
  border-radius: 100%;
  background-image: radial-gradient(
      circle at 0px 0px,
      #fff,
      #fff 30%,
      transparent 60%
    ),
    radial-gradient(circle at 0px 0px, #ffff34, #ffff34 50%, #000000 100%);
  top: 8.2%;
  left: 74.5%;
}

.decoration:nth-of-type(1) {
  left: 23.5%;
}

.decoration:nth-of-type(2) {
  top: 25%;
  left: 7.5%;
}

.decoration:nth-of-type(3) {
  top: 25%;
  left: 90.5%;
}

.decoration:nth-of-type(4) {
  top: 50%;
  left: 97%;
}

.decoration:nth-of-type(5) {
  top: 50%;
  left: 1%;
}

.decoration:nth-of-type(6) {
  top: 72.5%;
  left: 7%;
}

.decoration:nth-of-type(7) {
  top: 72.5%;
  left: 90.5%;
}

.decoration:nth-of-type(8) {
  top: 89.5%;
  left: 74.5%;
}

.decoration:nth-of-type(9) {
  top: 89.5%;
  left: 23.5%;
}

.decoration:nth-of-type(10) {
  top: 97%;
  left: 49%;
}

.arrow {
  position: absolute;
  width: 11%;
  height: 15%;
  background-image: url("./assets/arrow.png");
  background-size: cover;
  top: -3%;
  left: 44.5%;
  z-index: 2;
  transform-origin: center 34.6%;

  &.spinning {
    animation: tick 150ms infinite;
  }
}

@keyframes tick {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(-10deg);
  }
}

.border {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: black;
  border-radius: 100%;
  background-image: linear-gradient(
    to bottom left,
    black 42%,
    #fbf5ea 50%,
    black 58%
  );
  box-shadow: inset 1px 1px 1px 1px white, inset -1px -1px 1px 1px black;

  &::before {
    content: "";
    display: block;
    position: absolute;
    width: 16%;
    height: 7.84%;
    top: -1%;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    background-image: url("./assets/arrow-base.png");
    background-size: cover;
  }
}

.spin-button {
  content: "";
  display: block;
  position: absolute;
  width: 43.5%;
  height: 43.5%;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  border: 0;
  background-image: url("./assets/center.png");
  background-size: cover;
  border-radius: 100%;
  background-color: transparent;
  outline: 0;
  cursor: pointer;
  z-index: 2;
}

.content {
  display: block;
  position: absolute;
  border-radius: 100%;
  width: 92.8%;
  height: 92.8%;
  top: 3.6%;
  left: 3.6%;
  background: none;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    display: block;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    border-radius: 100%;
    box-shadow: inset 1px 1px 2px 1px black, inset -1px -1px 2px 1px white,
      inset 0 0 8px 4px black;
    pointer-events: none;
  }
}
</style>
